/**
 * ConfigureGateSummary.jsx
 * data-fit LLC — DecisionFlow
 * Stage 2 CONFIGURE — CONF-06: Configure gate summary
 * Branch: feature/compliance-lifecycle-foundation
 * Spec authority: ComponentSpec_CONFIGURE.md (S82) + MasterContext_S88_Close.md
 * Path: ui/src/components/tenant-admin/ConfigureGateSummary.jsx
 *
 * S89 audit: CLEAN — no patches required.
 * M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*
 * S495 fixes:
 *   - Authorization: Bearer token added to all fetch calls (OI-ONBOARDING-AUTH-HEADER-PATTERN)
 *   - Graceful 403 degradation on /tenant-config (OI-CONF03-TENANT-CONFIG-RBAC-WRONG-GATE)
 *   - Gate 3 logic corrected: ALL scope passes coverage gate (OI-CONF06-GATE3-ALL-SCOPE-LOGIC-INVERTED)
 *   - Gate 4 logic corrected: tenant-config 403 treated as implicit geo deferral (OI-CONF06-GEO-GATE-BLOCKS-ON-CONFIG-403)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';

const API_BASE = '/api/v1';

function normalizeResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function getErrorMessage(error) {
  return error?.message || 'Something went wrong.';
}

function buildGateChecks({ tier, periods, assignments, tenantConfig, tenantConfigUnavailable }) {
  const activePeriodCount = periods.filter((period) => period.is_active === true).length;

  const activeCompAdminAssignments = assignments.filter(
    (assignment) =>
      !assignment.revoked_at &&
      (assignment.role_name === 'comp_admin' ||
        assignment.user_type === 'comp_admin' ||
        assignment.roleName === 'comp_admin')
  );

  // S495 FIX (OI-CONF06-GATE3-ALL-SCOPE-LOGIC-INVERTED):
  // ALL scope (null BU + null segment) means maximum coverage — it passes the domain coverage gate.
  // Previous logic required at least one non-null scoped assignment, which incorrectly penalised
  // the broadest and most common valid configuration. Explicit scoping is advisory, not required.
  const scopedAssignments = activeCompAdminAssignments.filter(
    (assignment) =>
      assignment.business_unit_id != null ||
      assignment.segment_id != null ||
      assignment.businessUnitId != null ||
      assignment.segmentId != null
  );
  const allScopeOnly = activeCompAdminAssignments.length > 0 && scopedAssignments.length === 0;

  // S495 FIX (OI-CONF06-GEO-GATE-BLOCKS-ON-CONFIG-403):
  // If tenant-config is inaccessible (403 for comp_admin), treat geo scope as implicitly deferred.
  // A comp admin completing onboarding should not be hard-blocked by a permission gap on an
  // endpoint they don't own. Conservative blocking is worse than implicit deferral here.
  const geoScopeDeferred =
    tenantConfigUnavailable ||
    tenantConfig?.geo_scope_deferred === true ||
    tenantConfig?.geoScopeDeferred === true ||
    tenantConfig?.enterprise_geo_deferred === true;

  const geoScopeExists =
    tenantConfig?.geo_scope_exists === true ||
    tenantConfig?.geoScopeExists === true ||
    tenantConfig?.enterprise_geo_scope_exists === true;

  const gates = [
    {
      id: 'active_period',
      label: 'At least one fiscal period is active',
      description: 'CONF-02 must activate at least one fiscal period using is_active=true.',
      severity: 'hard',
      passed: activePeriodCount >= 1,
      evidence: `${activePeriodCount} active period${activePeriodCount === 1 ? '' : 's'} found`
    },
    {
      id: 'comp_admin_assignment',
      label: 'At least one comp admin assignment exists',
      description: 'A comp admin assignment is required before SETUP can open.',
      severity: 'hard',
      passed: activeCompAdminAssignments.length >= 1,
      evidence: `${activeCompAdminAssignments.length} active comp admin assignment${activeCompAdminAssignments.length === 1 ? '' : 's'} found`
    }
  ];

  if (tier === 'standard' || tier === 'enterprise') {
    gates.push({
      id: 'domain_coverage',
      label: 'Comp admin domain coverage confirmed',
      description: 'At least one active comp admin assignment must exist. ALL scope covers all BU×segment combinations and is valid.',
      severity: 'hard',
      // FIX: ALL scope (null BU + null segment) is full coverage — passes the gate
      passed: activeCompAdminAssignments.length >= 1,
      evidence: allScopeOnly
        ? `${activeCompAdminAssignments.length} comp admin assignment${activeCompAdminAssignments.length === 1 ? '' : 's'} at ALL scope — full BU×segment coverage`
        : scopedAssignments.length > 0
          ? `${scopedAssignments.length} scoped assignment${scopedAssignments.length === 1 ? '' : 's'} + ${activeCompAdminAssignments.length - scopedAssignments.length} ALL scope`
          : '0 comp admin assignments found',
      // Advisory only — soft signal when all assignments are ALL scope on enterprise
      advisory: tier === 'enterprise' && allScopeOnly
        ? 'All assignments use ALL scope. Consider adding BU or segment-scoped assignments if multiple comp admins should own distinct coverage areas.'
        : null
    });
  }

  if (tier === 'enterprise') {
    gates.push({
      id: 'enterprise_geo_scope',
      label: 'Enterprise geo scope exists or is deferred',
      description: 'Enterprise tenants require geo scope coverage unless explicitly deferred.',
      severity: 'hard',
      passed: geoScopeExists || geoScopeDeferred,
      evidence: geoScopeExists
        ? 'Geo scope exists'
        : tenantConfigUnavailable
          ? 'Geo scope check deferred — tenant configuration not accessible at current role'
          : geoScopeDeferred
            ? 'Geo scope deferred'
            : 'No geo scope evidence found'
    });
  }

  return gates;
}

export default function ConfigureGateSummary({ tenantId, tier, onBack, onComplete }) {
  const { token } = useAuth();

  const [gateChecks, setGateChecks] = useState([]);
  const [tenantConfig, setTenantConfig] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [tenantConfigUnavailable, setTenantConfigUnavailable] = useState(false);

  const authHeaders = useMemo(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchGateInputs = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    setTenantConfigUnavailable(false);

    try {
      const [periodsResponse, assignmentsResponse, tenantConfigResponse] = await Promise.all([
        fetch(`${API_BASE}/fiscal-periods`, { credentials: 'include', headers: authHeaders }),
        fetch(`${API_BASE}/user-role-assignments`, { credentials: 'include', headers: authHeaders }),
        fetch(`${API_BASE}/tenant-config`, { credentials: 'include', headers: authHeaders })
      ]);

      // Hard fail only on fiscal-periods and user-role-assignments
      // tenant-config 403 is expected for comp_admin — degrade gracefully
      const failed = [periodsResponse, assignmentsResponse].find((response) => !response.ok);
      if (failed) {
        const payload = await failed.json().catch(() => ({}));
        throw new Error(payload?.error?.message || 'Failed to load configure gates');
      }

      const [periodsPayload, assignmentsPayload] = await Promise.all([
        periodsResponse.json(),
        assignmentsResponse.json()
      ]);

      const nextPeriods = normalizeResponse(periodsPayload);
      const nextAssignments = normalizeResponse(assignmentsPayload);

      // Graceful 403 degradation on tenant-config
      let nextTenantConfig = {};
      let configUnavailable = false;
      if (tenantConfigResponse.ok) {
        const tenantConfigPayload = await tenantConfigResponse.json();
        nextTenantConfig = tenantConfigPayload?.data || tenantConfigPayload || {};
      } else {
        configUnavailable = true;
        setTenantConfigUnavailable(true);
      }

      setPeriods(nextPeriods);
      setAssignments(nextAssignments);
      setTenantConfig(nextTenantConfig);
      setGateChecks(buildGateChecks({
        tier,
        periods: nextPeriods,
        assignments: nextAssignments,
        tenantConfig: nextTenantConfig,
        tenantConfigUnavailable: configUnavailable
      }));
    } catch (error) {
      setLoadError(getErrorMessage(error));
      setGateChecks([]);
    } finally {
      setIsLoading(false);
    }
  }, [tier, authHeaders]);

  useEffect(() => { fetchGateInputs(); }, [fetchGateInputs]);

  const allHardGatesPass = useMemo(
    () => gateChecks.every((gate) => gate.severity !== 'hard' || gate.passed),
    [gateChecks]
  );

  const handleComplete = () => {
    if (!allHardGatesPass) return;
    window.dispatchEvent(
      new CustomEvent('decisionflow:navigate', {
        detail: { target: 'SETUP', source: 'CONFIGURE', tenantId, tier, gateChecks }
      })
    );
    if (onComplete) onComplete();
  };

  return (
    <section className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-sm">
      <div className="border-b border-[rgb(var(--color-border))] p-5">
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">CONF-06</p>
        <h2 className="mt-1 text-tok-xl font-semibold text-[rgb(var(--color-text))]">Configure gate summary</h2>
        <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Confirm all configure gates pass before opening SETUP.
        </p>
      </div>

      <div className="space-y-5 p-5">
        {loadError && (
          <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 p-4 text-tok-sm text-[rgb(var(--color-danger))]">
            <p className="font-semibold">Unable to load configure gates</p>
            <p className="mt-1">{loadError}</p>
            <button type="button" onClick={fetchGateInputs} className="mt-3 rounded-lg bg-[rgb(var(--color-danger))] px-3 py-2 text-white">Retry</button>
          </div>
        )}

        {tenantConfigUnavailable && !loadError && (
          <div className="rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 p-3 text-tok-sm text-[rgb(var(--color-warning))]">
            Tenant configuration is not accessible with your current role. Geo scope check has been deferred automatically.
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
            <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Tier</p>
            <p className="mt-1 text-tok-2xl font-semibold capitalize text-[rgb(var(--color-text))]">{tier}</p>
          </div>
          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
            <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Active periods</p>
            <p className="mt-1 text-tok-2xl font-semibold text-[rgb(var(--color-text))]">
              {periods.filter((period) => period.is_active === true).length}
            </p>
          </div>
          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
            <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Assignments</p>
            <p className="mt-1 text-tok-2xl font-semibold text-[rgb(var(--color-text))]">
              {assignments.filter((assignment) => !assignment.revoked_at).length}
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-[rgb(var(--color-border))]">
          <div className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Gate checks</h3>
          </div>
          <div className="divide-y divide-[rgb(var(--color-border))]">
            {isLoading ? (
              <div className="p-5 text-tok-sm text-[rgb(var(--color-text-muted))]">Loading gate checks...</div>
            ) : gateChecks.length === 0 ? (
              <div className="p-5 text-tok-sm text-[rgb(var(--color-text-muted))]">No gate checks available.</div>
            ) : (
              gateChecks.map((gate) => (
                <div key={gate.id} className="grid gap-3 p-4 md:grid-cols-[1fr_auto]">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-semibold text-[rgb(var(--color-text))]">{gate.label}</h4>
                      <span className={`rounded-full px-2 py-1 text-tok-xs font-medium ${gate.passed ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]' : 'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]'}`}>
                        {gate.passed ? 'Passed' : 'Blocked'}
                      </span>
                      <span className="rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-1 text-tok-xs font-medium uppercase text-[rgb(var(--color-text-muted))]">
                        {gate.severity}
                      </span>
                    </div>
                    <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">{gate.description}</p>
                    <p className="mt-2 text-tok-xs text-[rgb(var(--color-text-muted))]">Evidence: {gate.evidence}</p>
                    {gate.advisory && (
                      <p className="mt-2 rounded-lg bg-[rgb(var(--color-warning))]/10 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))]">
                        Advisory: {gate.advisory}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center md:justify-end">
                    <div className={`h-3 w-3 rounded-full ${gate.passed ? 'bg-[rgb(var(--color-success))]' : 'bg-[rgb(var(--color-danger))]'}`} aria-label={gate.passed ? 'Passed' : 'Blocked'} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {!allHardGatesPass && gateChecks.length > 0 && (
          <div className="rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 p-4 text-tok-sm text-[rgb(var(--color-warning))]">
            SETUP is blocked until all hard gates pass.
          </div>
        )}

        {tenantConfig && Object.keys(tenantConfig).length > 0 && (
          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 text-tok-sm text-[rgb(var(--color-text-muted))]">
            <p className="font-semibold text-[rgb(var(--color-text))]">Tenant config loaded</p>
            <p className="mt-1">Configuration evidence is available for configure gate evaluation.</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] p-5">
        <button type="button" onClick={onBack} className="rounded-xl border border-[rgb(var(--color-border))] px-5 py-2.5 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]">
          Back
        </button>
        <div className="flex gap-3">
          <button type="button" onClick={fetchGateInputs} className="rounded-xl border border-[rgb(var(--color-border))] px-5 py-2.5 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]">
            Refresh Gates
          </button>
          <button type="button" disabled={!allHardGatesPass || isLoading} onClick={handleComplete} className="rounded-xl bg-[rgb(var(--color-primary))] px-5 py-2.5 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:cursor-not-allowed disabled:bg-[rgb(var(--color-disabled))]">
            Open SETUP
          </button>
        </div>
      </div>
    </section>
  );
}
